import { ChatStore } from "@/stores/ChatStore";

interface SessionResponse {
  session_id: string;
}

interface ErrorResponse {
  error: string;
}

export async function fetchSessionId(): Promise<string> {
  console.log("🔑 [fetchSessionId] START");
  
  const storedSessionId = ChatStore.getState().sessionId;
  if (storedSessionId) {
    console.log("✅ [fetchSessionId] Using cached session ID:", storedSessionId);
    // If session ID is already available, return it immediately
    return storedSessionId;
  }
  
  console.log("📝 [fetchSessionId] No cached session ID, fetching new one");
  
  if (ChatStore.getState().isForcedToInterrupt) {
    console.log("⚠️ [fetchSessionId] Request aborted due to interruption");
    throw new Error("Request aborted due to interruption");
  }

  const controller = new AbortController();
  ChatStore.getState().registerAbortController(controller);
  console.log("✅ [fetchSessionId] AbortController created");
  
  try {
    const endpoint = "/api/schadenki/session";
    console.log("📡 [fetchSessionId] Sending POST to:", endpoint);
    
    const response = await fetch(
      endpoint,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
        signal: controller.signal
      }
    );

    console.log("📥 [fetchSessionId] Response received:", {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    });

    if (!response.ok) {
      const responseText = await response.text();
      console.error("❌ [fetchSessionId] Error response:", responseText);
      
      let errorMessage: string;
      try {
        const errorData = JSON.parse(responseText) as ErrorResponse;
        errorMessage = errorData.error || `Server error: ${response.status}`;
      } catch {
        errorMessage = `Failed to fetch session: ${response.status} ${response.statusText}`;
      }
      console.error("❌ [fetchSessionId] Error message:", errorMessage);
      throw new Error(errorMessage);
    }

    const data = await response.json() as SessionResponse;
    console.log("✅ [fetchSessionId] Response data:", data);
    
    if (!data.session_id) {
      console.error("❌ [fetchSessionId] Response missing session_id");
      throw new Error("Response missing session_id");
    }
    
    console.log("✅ [fetchSessionId] Storing session ID:", data.session_id);
    ChatStore.getState().setSessionId(data.session_id);
    return data.session_id;
  } catch (error) {
    // Check if this was an abort error
    if (error instanceof DOMException && error.name === 'AbortError') {
      console.log('⚠️ [fetchSessionId] Session fetch aborted due to user interruption');
      throw error; // Re-throw to be handled by the caller
    }
    
    console.error('❌ [fetchSessionId] Error fetching session ID:', error);
    console.error('❌ [fetchSessionId] Error stack:', error instanceof Error ? error.stack : 'N/A');
    throw error;
  } finally {
    // Clean up
    const controllers = ChatStore.getState().abortControllers;
    const index = controllers.indexOf(controller);
    if (index > -1) {
      controllers.splice(index, 1);
    }
    console.log("🧹 [fetchSessionId] Cleanup complete");
  }
}
